import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Wallet, AlertTriangle, CheckCircle2, Clock, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { fmtEUR, INVOICE_STATUS_META } from "../../lib/invoiceConstants";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";

const Kpi = ({ icon: Icon, label, value, color }) => (
  <Card className="p-4 flex items-center gap-3">
    <div className={`rounded-lg p-2 ${color}`}><Icon className="w-5 h-5 text-white" /></div>
    <div><p className="text-xl font-bold leading-none">{value}</p><p className="text-xs text-muted-foreground mt-1">{label}</p></div>
  </Card>
);

const PaymentsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/payments/summary");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remind = async (clientId) => {
    setBusy(clientId + "r");
    try {
      const { data: r } = await API.post(`/payments/remind/${clientId}`);
      toast.success(r.message || "Relance envoyée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const statement = async (clientId) => {
    setBusy(clientId + "s");
    try {
      const { data: rc } = await API.post(`/statements/${clientId}`);
      const res = await API.get(`/statements/${rc.id}/pdf`, { responseType: "blob" });
      const u = window.URL.createObjectURL(res.data);
      window.open(u, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(u), 10000);
      toast.success(`Relevé ${rc.number} généré`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  if (loading || !data) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>;
  }

  return (
    <div data-testid="payments-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Wallet} label="Total dû" value={fmtEUR(data.totalDue)} color="bg-[hsl(24,95%,53%)]" />
        <Kpi icon={AlertTriangle} label="Factures impayées" value={data.unpaidInvoices} color="bg-orange-500" />
        <Kpi icon={CheckCircle2} label="Paiements réglés" value={data.paidPayments} color="bg-green-500" />
        <Kpi icon={Clock} label="Paiements en attente" value={data.pendingPayments} color="bg-blue-500" />
      </div>

      <h3 className="font-semibold text-slate-800 mb-2">Solde par client</h3>
      <Card className="overflow-hidden mb-6">
        {data.byClient.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">Aucun impayé. 🎉</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Client</TableHead><TableHead>Impayées</TableHead><TableHead>Solde dû</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.byClient.map((c) => (
                <TableRow key={c.clientId || c.name} data-testid={`balance-row-${c.clientId}`}>
                  <TableCell className="text-sm font-medium">{c.name || "—"}</TableCell>
                  <TableCell className="text-sm">{c.unpaidCount}</TableCell>
                  <TableCell className="text-sm font-semibold text-orange-700">{fmtEUR(c.balance)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      <Button variant="outline" size="sm" disabled={!c.clientId || busy === c.clientId + "r"} onClick={() => remind(c.clientId)} data-testid={`remind-${c.clientId}`}>
                        {busy === c.clientId + "r" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Relance
                      </Button>
                      <Button variant="outline" size="sm" disabled={!c.clientId || busy === c.clientId + "s"} onClick={() => statement(c.clientId)} data-testid={`statement-${c.clientId}`}>
                        {busy === c.clientId + "s" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Relevé RC
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <h3 className="font-semibold text-slate-800 mb-2">Paiements groupés</h3>
      <Card className="overflow-hidden">
        {data.groupedPayments.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">Aucun paiement groupé.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Montant</TableHead><TableHead>Factures</TableHead><TableHead>Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.groupedPayments.map((p) => (
                <TableRow key={p.id} data-testid={`pg-row-${p.number}`}>
                  <TableCell className="font-mono text-xs font-semibold text-[hsl(207,78%,42%)]">{p.number}</TableCell>
                  <TableCell className="text-sm">{p.buyerName || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{fmtEUR(p.totalAmount)}</TableCell>
                  <TableCell className="text-sm">{p.invoices?.length}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded border ${p.status === "paye" ? "bg-green-100 text-green-800 border-green-200" : p.status === "en_attente" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                      {p.status === "paye" ? "Payé" : p.status === "en_attente" ? "En attente" : p.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default PaymentsTab;
